// @oasis-permission-inherits: /videos (mirage-videos:view)
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { InfiniteQueryPagination } from "~/components/infinite-query-pagination";
import { Skeleton } from "~/components/ui/skeleton";
import { DpoSegmentFlagDialog } from "~/modules/mirage/components/dpo-segment-flag-dialog";
import { DpoVideoInfoCard } from "~/modules/mirage/components/dpo-video-info-card";
import ResponsiveGrid from "~/modules/mirage/components/responsive-grid";
import { toggleVideoBookmarkMutation } from "~/modules/mirage/mutations";
import { fetchBookmarkedVideosQuery } from "~/modules/mirage/queries";
import { useDpoFlagging } from "~/modules/mirage/use-dpo-flagging";
import { VideoCard } from "~/modules/videos/components/video-card";
import { hideProductInfoStorage } from "~/modules/videos/storage";

export const Route = createFileRoute("/_dash/videos/bookmarked")({
  component: RouteComponent,
});

function RouteComponent() {
  const [page, setPage] = useState(1);

  const [hideProductInfoState] = useState<boolean>(() => {
    const stored = hideProductInfoStorage.get();
    return stored ?? false;
  });

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, refetch } =
    fetchBookmarkedVideosQuery.useInfiniteQuery({
      params: {},
    });

  const totalPages = data?.pages.length ?? 0;
  const [currentPageIndex, setCurrentPageIndex] = useState(
    page && page <= totalPages ? page - 1 : 0
  );

  const currentPageData = data?.pages[currentPageIndex];

  const onPreviousPage = () => {
    const previousPageIndex = Math.max(0, currentPageIndex - 1);
    setCurrentPageIndex(previousPageIndex);
    setPage(previousPageIndex + 1);
  };

  const onNextPage = async () => {
    const nextPageIndex = currentPageIndex + 1;
    setCurrentPageIndex(nextPageIndex);
    setPage(nextPageIndex + 1);
    if (nextPageIndex === totalPages) {
      await fetchNextPage();
    }
  };

  const onPageChange = (pageIndex: number) => setCurrentPageIndex(pageIndex);

  const toggleBookmark = toggleVideoBookmarkMutation.useMutation({
    onSuccess: (data) => {
      if (!data.bookmarked) {
        toast.success("Video removed from bookmarks");
        // Refetch to update the list
        refetch();
      }
    },
    onError: () => {
      toast.error("Failed to update bookmark");
    },
  });

  const handleToggleBookmark = (videoId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleBookmark.mutate({
      pathParams: { videoId },
      params: {},
    });
  };

  const dpoFlagging = useDpoFlagging();
  const [dpoFlagVideoId, setDpoFlagVideoId] = useState<string | null>(null);

  const handleFlagForDpo = (videoId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDpoFlagVideoId(videoId);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">Bookmarked Videos</h1>
      </div>

      <div className="flex w-full h-full justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Link to="/videos" className="text-sm text-muted-foreground hover:underline">
            ← View All Videos
          </Link>
        </div>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      <ResponsiveGrid gap={16} maxColumns={5} itemMinWidth={200} itemMinHeight={200}>
        {(isLoading || isFetchingNextPage) &&
          Array.from({ length: 20 }).map((_, index) => (
            <Skeleton key={index} className="aspect-9/16 w-full h-full" />
          ))}
        {currentPageData?.videos.page?.map((video) => (
          <VideoCard
            key={video.id}
            video={video}
            linkTo="/videos/view/$videoId"
            linkParams={{ videoId: video.id }}
            linkSearch={{
              from: "bookmarked",
            }}
            hideProductInfo={hideProductInfoState}
            onToggleBookmark={handleToggleBookmark}
            onFlagForDpo={handleFlagForDpo}
            isFlagForDpoPending={dpoFlagging.isFlagPending(video.id)}
            flagHoverInfo={<DpoVideoInfoCard videoId={video.id} flagging={dpoFlagging} />}
          />
        ))}
        {!isLoading && currentPageData?.videos.page?.length === 0 && (
          <div className="flex text-sm text-muted-foreground">No bookmarked videos</div>
        )}
      </ResponsiveGrid>
      <DpoSegmentFlagDialog
        videoId={dpoFlagVideoId}
        open={dpoFlagVideoId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDpoFlagVideoId(null);
          }
        }}
        flagging={dpoFlagging}
      />
      {totalPages > 0 && !isLoading && !isFetchingNextPage && (
        <div className="flex justify-end w-full mb-16">
          <div>
            <InfiniteQueryPagination
              onPreviousPage={onPreviousPage}
              onNextPage={onNextPage}
              onPageChange={onPageChange}
              currentPageIndex={currentPageIndex}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              totalPages={totalPages}
            />
          </div>
        </div>
      )}
    </div>
  );
}
